/**
 * Presets for the raw passthrough console. The passthrough is a thin forwarder:
 * it injects only credentials (TecDoc x-api-key; Kromeda nothing), NOT the
 * business params. So Kromeda calls need a session key ({s} in the path) and
 * TecDoc bodies carry provider/lang — both editable here. A "managed" console
 * that uses the server's configured session/provider pairs with the next-pass
 * request-log work.
 */
export type Method = 'GET' | 'POST';

export interface Preset {
  label: string;
  method: Method;
  path: string; // relative to /{source}
  body?: string;
}

export interface ConsoleCfg {
  note: string;
  needsSession: boolean;
  presets: Preset[];
}

const td = (op: string, params: Record<string, unknown>) =>
  JSON.stringify({ [op]: { provider: 0, lang: 'it', articleCountry: 'it', ...params } }, null, 2);

export const CONSOLE: Record<'kromeda' | 'tecdoc', ConsoleCfg> = {
  kromeda: {
    needsSession: true,
    note:
      'Path-based GET proxy. Calls need a session key ({s} in the path) — paste one, or run CreateSessionKey (with your credentials) and copy result[1] into the Session field.',
    presets: [
      { label: 'LS_FreeSearch', method: 'GET', path: '/LS_FreeSearch/{s}/552400047' },
      { label: 'LS_GetCross', method: 'GET', path: '/LS_GetCross/{s}/348/552400047' },
      { label: 'LS_GetBrands', method: 'GET', path: '/LS_GetBrands/{s}' },
      { label: 'LS_GetModels', method: 'GET', path: '/LS_GetModels/{s}/74' },
      { label: 'LS_GetVehicles', method: 'GET', path: '/LS_GetVehicles/{s}/2154' },
      { label: 'CreateSessionKey', method: 'GET', path: '/CreateSessionKey/USER/PASS' },
    ],
  },
  tecdoc: {
    needsSession: false,
    note:
      'Single JSON endpoint (POST). The proxy injects x-api-key; set provider/lang in the body to your account values (0/it are placeholders).',
    presets: [
      {
        label: 'getArticles · OE/cross (type 10)',
        method: 'POST',
        path: '/TecdocToCatDLB.jsonEndpoint',
        body: td('getArticles', { searchType: 10, searchMatchType: 'exact', searchQuery: '552400047' }),
      },
      {
        label: 'getArticles · article (type 0)',
        method: 'POST',
        path: '/TecdocToCatDLB.jsonEndpoint',
        body: td('getArticles', { searchType: 0, searchMatchType: 'exact', searchQuery: '0092S40020' }),
      },
      {
        label: 'getManufacturers',
        method: 'POST',
        path: '/TecdocToCatDLB.jsonEndpoint',
        body: td('getManufacturers', {}),
      },
      {
        label: 'getModelSeries',
        method: 'POST',
        path: '/TecdocToCatDLB.jsonEndpoint',
        body: td('getModelSeries', { manuId: 74 }),
      },
    ],
  },
};
